import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Shield, ArrowLeft, Plus, Trash2, Calculator, TrendingDown,
  DollarSign, Calendar, ArrowRight, Save, BookOpen, AlertTriangle, ChevronRight
} from 'lucide-react';
import { Progress } from '../components/ui/progress';
import axios from 'axios';
import AppFooter from '../components/AppFooter';
import BureauBadges from '../components/BureauBadges';
import CrossBureauCompareModal from '../components/CrossBureauCompareModal';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACCOUNT_TYPES = [
  'Credit Card', 'Auto Loan', 'Student Loan', 'Personal Loan',
  'Medical Debt', 'Mortgage', 'Other'
];

export default function DebtCalculatorPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [debts, setDebts] = useState([]);
  const [extraPayment, setExtraPayment] = useState(0);
  const [method, setMethod] = useState('snowball');
  const [results, setResults] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [compareTarget, setCompareTarget] = useState(null);   // { creditor, last4 } or null
  const [conflicts, setConflicts] = useState([]);             // merged accounts whose bureaus disagree

  useEffect(() => {
    loadSavedDebts();
    loadConflicts();

    // AUTO-REFRESH: listen for cross-tab + same-tab analysis updates so a new
    // credit-report upload flows into the calculator without a manual click.
    const onAnalysisUpdated = () => {
      loadFromReport({ silent: false, isRefresh: true });
      loadConflicts();
    };
    const onStorage = (e) => {
      if (e.key === 'selah:analysis-updated-at') onAnalysisUpdated();
    };
    window.addEventListener('selah:analysis-updated', onAnalysisUpdated);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('selah:analysis-updated', onAnalysisUpdated);
      window.removeEventListener('storage', onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConflicts = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/debt/conflicts`);
      setConflicts(data.conflicts || []);
    } catch { /* silent — pill just stays hidden */ }
  };

  const loadSavedDebts = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/debt/saved`);
      if (data.debts && data.debts.length > 0) {
        setDebts(data.debts);
        setExtraPayment(data.extra_payment || 0);
        setMethod(data.method || 'snowball');
      } else {
        // Auto-populate from credit report if no saved debts
        await loadFromReport();
      }
    } catch (e) {
      console.error('Failed to load saved debts');
    } finally {
      setLoadingSaved(false);
    }
  };

  const loadFromReport = async ({ silent = true, isRefresh = false } = {}) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/debt/from-report`);
      if (data.debts && data.debts.length > 0) {
        setDebts(data.debts);
        if (!silent) {
          const merged = data.merged_bureau_duplicates || 0;
          const msg = isRefresh
            ? `New report analyzed — refreshed ${data.count} debt${data.count === 1 ? '' : 's'}`
            : `Loaded ${data.count} debt${data.count === 1 ? '' : 's'} from your credit report`;
          toast.success(
            msg,
            merged > 0
              ? { description: `${merged} duplicate${merged === 1 ? '' : 's'} across bureaus merged into single rows.` }
              : undefined,
          );
        }
      } else if (!silent) {
        toast.info('No debts found in your credit report yet. Upload one from the dashboard first.');
      }
    } catch (e) {
      console.error('Failed to load debts from report');
      if (!silent) toast.error('Could not load debts from report.');
    }
  };

  const addDebt = () => {
    setDebts(prev => [...prev, {
      creditor_name: '',
      balance: 0,
      interest_rate: 0,
      minimum_payment: 0,
      account_type: 'Credit Card'
    }]);
  };

  const removeDebt = (index) => {
    setDebts(prev => prev.filter((_, i) => i !== index));
  };

  const updateDebt = (index, field, value) => {
    setDebts(prev => prev.map((debt, i) =>
      i === index ? { ...debt, [field]: value } : debt
    ));
  };

  const calculatePayoff = async () => {
    if (debts.length === 0) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/debt/compare`, {
        debts,
        extra_payment: parseFloat(extraPayment) || 0,
        method
      }, {});
      setComparison(data);
      setResults(data[method]);
    } catch (e) {
      console.error('Calculation failed:', e);
    } finally {
      setLoading(false);
    }
  };

  const saveDebts = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/debt/save`, {
        debts,
        extra_payment: parseFloat(extraPayment) || 0,
        method
      }, {});
    } catch (e) {
      console.error('Save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  const totalDebt = debts.reduce((sum, d) => sum + (parseFloat(d.balance) || 0), 0);
  const totalMinPayment = debts.reduce((sum, d) => sum + (parseFloat(d.minimum_payment) || 0), 0);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="debt-calculator-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">Selah Slate&trade;</span>
            </div>
            <div className="flex items-center space-x-3">
              <Link to="/faith-strategies" className="text-[#B76E79] hover:text-[#9e5c66] font-['Manrope'] text-sm font-medium hidden sm:inline" data-testid="faith-strategies-link">
                <BookOpen className="w-4 h-4 inline mr-1" strokeWidth={1.5} />Faith Strategies
              </Link>
              <Button variant="ghost" onClick={handleLogout} className="text-[#36454F]/70 hover:text-[#36454F]">Sign Out</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/dashboard" className="inline-flex items-center text-[#36454F]/65 hover:text-[#36454F] font-['Manrope'] mb-6 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />Back to Dashboard
        </Link>

        <div className="mb-8 animate-fade-in-up">
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#36454F]">
            Debt Freedom Calculator
          </h1>
          <p className="mt-2 text-[#36454F]/65 font-['Manrope']">
            "The rich rules over the poor, and the borrower is the slave of the lender." — Proverbs 22:7
          </p>
        </div>

        {/* Conflict pill — surfaces fresh cross-bureau disagreements so the user
            notices without hunting through the debt list. */}
        {conflicts.length > 0 && (
          <button
            onClick={() => setCompareTarget({ creditor: conflicts[0].creditor, last4: conflicts[0].last4 })}
            className="w-full mb-6 flex items-start gap-3 p-4 rounded-sm border border-[#B76E79]/30 bg-gradient-to-r from-[#B76E79]/8 to-white hover:border-[#B76E79]/60 hover:bg-[#B76E79]/5 transition-colors text-left animate-fade-in-up"
            data-testid="conflict-pill"
          >
            <AlertTriangle className="w-5 h-5 text-[#B76E79] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <div className="flex-1 min-w-0">
              <p className="font-['Manrope'] font-semibold text-[#36454F] text-sm">
                {conflicts.length === 1
                  ? `1 account disagrees across bureaus — review?`
                  : `${conflicts.length} accounts disagree across bureaus — review?`}
              </p>
              <p className="text-xs text-[#36454F]/60 font-['Manrope'] mt-0.5">
                {conflicts.slice(0, 3).map((c) => c.creditor).join(', ')}
                {conflicts.length > 3 && ` +${conflicts.length - 3} more`}
                {' '}· tap to see each bureau's version and dispute the wrong one.
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-[#B76E79] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          </button>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Debt Input Section */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="border-[#36454F]/15 bg-white animate-fade-in-up">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-['Playfair_Display'] text-xl text-[#36454F] flex items-center">
                  <DollarSign className="w-5 h-5 mr-2 text-[#B76E79]" strokeWidth={1.5} />
                  Your Debts
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => loadFromReport({ silent: false })}
                    size="sm"
                    variant="outline"
                    className="border-[#36454F]/20 text-[#36454F] hover:bg-[#36454F]/5 font-['Manrope'] rounded-sm h-8"
                    data-testid="pull-from-report-btn"
                    title="Auto-fill this list from your uploaded credit report — bureau duplicates are merged."
                  >
                    Pull from report
                  </Button>
                  <Button onClick={addDebt} size="sm" className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm h-8" data-testid="add-debt-btn">
                    <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} />Add Debt
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {debts.length === 0 && !loadingSaved && (
                  <div className="text-center py-8">
                    <DollarSign className="w-12 h-12 text-[#36454F]/20 mx-auto mb-3" strokeWidth={1.5} />
                    <p className="text-[#36454F]/50 font-['Manrope'] mb-4">Add your debts to get started</p>
                    <Button onClick={addDebt} className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm">
                      <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} />Add Your First Debt
                    </Button>
                  </div>
                )}

                {debts.map((debt, index) => (
                  <div key={index} className="p-4 border border-[#36454F]/10 rounded-sm bg-[#F5F5F5] space-y-3" data-testid={`debt-entry-${index}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <span className="text-xs uppercase tracking-wider text-[#36454F]/50 font-['Manrope']">
                          Debt {index + 1}
                        </span>
                        {debt.account_last4 && (
                          <span
                            className="text-[10px] font-['JetBrains_Mono'] text-[#36454F]/55 bg-white border border-[#36454F]/10 px-1.5 py-0.5 rounded"
                            data-testid={`debt-last4-${index}`}
                          >
                            •••• {debt.account_last4}
                          </span>
                        )}
                        {debt.bureaus && debt.bureaus.length > 0 && (
                          <BureauBadges bureaus={debt.bureaus} size="xs" />
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {debt.bureaus && debt.bureaus.length > 1 && debt.account_last4 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setCompareTarget({ creditor: debt.creditor_name, last4: debt.account_last4 })}
                            className="text-[#B76E79] hover:text-[#9e5c66] hover:bg-[#B76E79]/8 h-8 px-2 text-xs font-['Manrope'] rounded-sm"
                            data-testid={`compare-bureaus-btn-${index}`}
                            title="See how each bureau reports this account and dispute only the ones that are wrong."
                          >
                            Compare bureaus →
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => removeDebt(index)} className="text-[#D9534F] hover:text-[#D9534F]/80 h-8 w-8 p-0" data-testid={`remove-debt-${index}`}>
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-['Manrope'] text-[#36454F]/70">Creditor Name</Label>
                        <Input value={debt.creditor_name} onChange={(e) => updateDebt(index, 'creditor_name', e.target.value)} placeholder="e.g., Chase Visa" className="h-10 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope'] text-sm" data-testid={`debt-name-${index}`} />
                      </div>
                      <div>
                        <Label className="text-xs font-['Manrope'] text-[#36454F]/70">Account Type</Label>
                        <Select value={debt.account_type} onValueChange={(v) => updateDebt(index, 'account_type', v)}>
                          <SelectTrigger className="h-10 border-[#36454F]/15 focus:border-[#B76E79] rounded-sm font-['Manrope'] text-sm" data-testid={`debt-type-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ACCOUNT_TYPES.map(t => <SelectItem key={t} value={t} className="font-['Manrope']">{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-['Manrope'] text-[#36454F]/70">Balance ($)</Label>
                        <Input type="number" min="0" step="0.01" value={debt.balance || ''} onChange={(e) => updateDebt(index, 'balance', parseFloat(e.target.value) || 0)} placeholder="5000" className="h-10 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope'] text-sm" data-testid={`debt-balance-${index}`} />
                      </div>
                      <div>
                        <Label className="text-xs font-['Manrope'] text-[#36454F]/70">Interest Rate (%)</Label>
                        <Input type="number" min="0" step="0.1" value={debt.interest_rate || ''} onChange={(e) => updateDebt(index, 'interest_rate', parseFloat(e.target.value) || 0)} placeholder="18.99" className="h-10 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope'] text-sm" data-testid={`debt-rate-${index}`} />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-xs font-['Manrope'] text-[#36454F]/70">Minimum Monthly Payment ($)</Label>
                        <Input type="number" min="0" step="0.01" value={debt.minimum_payment || ''} onChange={(e) => updateDebt(index, 'minimum_payment', parseFloat(e.target.value) || 0)} placeholder="100" className="h-10 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope'] text-sm w-1/2" data-testid={`debt-payment-${index}`} />
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Calculator Controls */}
          <div className="space-y-4">
            {/* Summary */}
            <Card className="border-[#36454F]/15 bg-white animate-fade-in-up animation-delay-100">
              <CardHeader>
                <CardTitle className="font-['Playfair_Display'] text-lg text-[#36454F]">Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-[#F5F5F5] rounded-sm">
                  <span className="text-xs text-[#36454F]/50 font-['Manrope'] block">Total Debt</span>
                  <span className="text-2xl font-['Manrope'] font-semibold text-[#36454F]">${totalDebt.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="p-3 bg-[#F5F5F5] rounded-sm">
                  <span className="text-xs text-[#36454F]/50 font-['Manrope'] block">Total Min. Payments</span>
                  <span className="text-lg font-['Manrope'] font-semibold text-[#36454F]">${totalMinPayment.toLocaleString('en-US', { minimumFractionDigits: 2 })}/mo</span>
                </div>

                <div>
                  <Label className="text-sm font-['Manrope'] text-[#36454F]/80">Extra Monthly Payment</Label>
                  <Input type="number" min="0" step="10" value={extraPayment || ''} onChange={(e) => setExtraPayment(parseFloat(e.target.value) || 0)} placeholder="0" className="mt-1 h-10 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope']" data-testid="extra-payment-input" />
                </div>

                <div>
                  <Label className="text-sm font-['Manrope'] text-[#36454F]/80">Payoff Method</Label>
                  <Select value={method} onValueChange={setMethod}>
                    <SelectTrigger className="mt-1 border-[#36454F]/15 focus:border-[#B76E79] rounded-sm font-['Manrope']" data-testid="method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="snowball" className="font-['Manrope']">Snowball (Smallest First)</SelectItem>
                      <SelectItem value="avalanche" className="font-['Manrope']">Avalanche (Highest Rate First)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-[#36454F]/50 font-['Manrope']">
                    {method === 'snowball'
                      ? 'Pays smallest balances first for quick wins and motivation'
                      : 'Pays highest interest rates first to minimize total cost'}
                  </p>
                </div>

                <Button onClick={calculatePayoff} disabled={loading || debts.length === 0} className="w-full h-12 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] font-medium rounded-sm" data-testid="calculate-btn">
                  {loading ? (
                    <span className="flex items-center"><svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Calculating...</span>
                  ) : (
                    <span className="flex items-center justify-center"><Calculator className="w-5 h-5 mr-2" strokeWidth={1.5} />Calculate Payoff</span>
                  )}
                </Button>

                <Button onClick={saveDebts} disabled={saving || debts.length === 0} variant="outline" className="w-full border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white font-['Manrope'] rounded-sm" data-testid="save-debts-btn">
                  <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />{saving ? 'Saving...' : 'Save My Debts'}
                </Button>
              </CardContent>
            </Card>

            {/* Faith Insight */}
            <Card className="border-[#E0BFB8] bg-[#E0BFB8]/10 animate-fade-in-up animation-delay-200">
              <CardContent className="p-6">
                <p className="font-['Playfair_Display'] text-sm italic text-[#36454F] mb-2">
                  "Owe no one anything, except to love each other, for the one who loves another has fulfilled the law."
                </p>
                <p className="text-xs text-[#36454F]/60 font-['Manrope']">Romans 13:8 (ESV)</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Results Section */}
        {comparison && (
          <div className="mt-8 space-y-6 animate-fade-in-up">
            {/* Comparison */}
            <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F]">Your Path to Freedom</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {['snowball', 'avalanche'].map((m) => {
                const r = comparison[m];
                const isActive = method === m;
                return (
                  <Card key={m} className={`border-2 transition-all duration-300 cursor-pointer ${isActive ? 'border-[#B76E79] bg-white' : 'border-[#36454F]/10 bg-white'}`} onClick={() => { setMethod(m); setResults(comparison[m]); }} data-testid={`method-card-${m}`}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#36454F] capitalize">{m} Method</h3>
                        {isActive && <span className="text-xs px-2 py-1 bg-[#B76E79] text-white rounded font-['Manrope']">Selected</span>}
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-[#36454F]/65 font-['Manrope']">Debt-Free Date</span>
                          <span className="font-['Manrope'] font-semibold text-[#36454F]">{r.debt_free_date}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-[#36454F]/65 font-['Manrope']">Total Months</span>
                          <span className="font-['Manrope'] font-semibold text-[#36454F]">{r.total_months}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-[#36454F]/65 font-['Manrope']">Total Interest</span>
                          <span className="font-['Manrope'] font-semibold text-[#D9534F]">${r.total_interest.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-[#36454F]/65 font-['Manrope']">Total Paid</span>
                          <span className="font-['Manrope'] font-semibold text-[#36454F]">${r.total_paid.toLocaleString()}</span>
                        </div>
                      </div>
                      <p className="mt-4 text-xs text-[#36454F]/50 font-['Manrope'] italic">
                        {m === 'snowball' ? '"Small wins build faith and momentum."' : '"Wisdom says tackle the costliest burden first."'}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Interest Savings */}
            {comparison.snowball && comparison.avalanche && (
              <Card className="border-[#36454F]/15 bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-2">
                    <TrendingDown className="w-5 h-5 text-[#4CAF50]" strokeWidth={1.5} />
                    <span className="font-['Manrope'] font-semibold text-[#36454F]">
                      Avalanche saves ${Math.abs(comparison.snowball.total_interest - comparison.avalanche.total_interest).toLocaleString()} in interest
                    </span>
                  </div>
                  <p className="text-sm text-[#36454F]/65 font-['Manrope']">
                    Snowball may cost more in interest but provides faster emotional wins. Choose what keeps you faithful to the journey.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Payoff Timeline */}
            {results && results.debts && (
              <Card className="border-[#36454F]/15 bg-white">
                <CardHeader>
                  <CardTitle className="font-['Playfair_Display'] text-xl text-[#36454F] flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-[#B76E79]" strokeWidth={1.5} />
                    Payoff Order
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {results.debts.map((debt, index) => (
                      <div key={index} className="flex items-center space-x-4 p-3 bg-[#F5F5F5] rounded-sm" data-testid={`payoff-item-${index}`}>
                        <div className="w-8 h-8 rounded-full bg-[#B76E79] text-white flex items-center justify-center font-['Manrope'] font-semibold text-sm flex-shrink-0">
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-['Manrope'] font-medium text-[#36454F] truncate">{debt.creditor_name || 'Unnamed'}</p>
                          <p className="text-xs text-[#36454F]/50 font-['Manrope']">
                            ${debt.original_balance.toLocaleString()} at {debt.interest_rate}% APR
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-['Manrope'] font-semibold text-[#36454F]">{debt.payoff_date}</p>
                          <p className="text-xs text-[#D9534F] font-['Manrope']">${debt.total_interest.toLocaleString()} interest</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Faith Encouragement */}
            <Card className="border-[#E0BFB8] bg-[#E0BFB8]/10">
              <CardContent className="p-8 text-center">
                <p className="font-['Playfair_Display'] text-lg italic text-[#36454F] mb-3">
                  "For I know the plans I have for you, declares the Lord, plans for welfare and not for evil, to give you a future and a hope."
                </p>
                <p className="text-sm text-[#36454F]/60 font-['Manrope'] mb-4">Jeremiah 29:11 (ESV)</p>
                <p className="text-sm text-[#36454F]/80 font-['Manrope']">
                  Your debt-free journey is not just about numbers — it's about the freedom God intends for your life.
                  Stay faithful, stay consistent, and watch what He does.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
      {compareTarget && (
        <CrossBureauCompareModal
          open={!!compareTarget}
          onClose={() => setCompareTarget(null)}
          creditor={compareTarget.creditor}
          last4={compareTarget.last4}
        />
      )}
      <AppFooter />
    </div>
  );
}
